<template lang="pug">
  .width-100
    .row.m4-bottom-2(v-if='label')
      label.text-normal
        | {{ `${ label } ${ showRequiredMark ? `${$t('inputs.requiredMark')}` : ''}` }}
    .select-container(v-on-clickaway='hideOptions' :class='{ edition: edition }')
      button.select-button(
        type='button'
        @blur='verifyValidation'
        @click='toggleOptions'
        :disabled='disabled'
        :class='{ transparent, "no-spacing": noSpacing, "border": withBorder, "error-container": error, filled: filled && selectedOption, small }'
      )
        span.font-class(v-if='labelIn') {{ labelIn + ': ' }}
        span.font-class.selected(
          v-if='selectedOption'
          v-html='selectedOption[labelName]'
          :class='{ "text-with-overflow": handleTextOverflow, "text-small": small, "text-normal": !small }'
        )
        span.dark-gray(v-else :class='{ "text-big": big, "text-small": small, "text-normal": !small && !big }') {{ placeholder }}
        simple-svg.direction.m-left-05.dark-gray-icon(
          :filepath='arrowDown'
          :class='{ open: showOptions }'
          alt='show options'
          width='16px'
          height='16px'
        )
      .options-container.kimple-scroll.absolute(v-if='showOptions' :class='{small}')
        button.option.text-normal(
          :class='{small, disabled: option.disabled}'
          type='button'
          v-for='option in optionsToSelect'
          :key='option.onClick ? option[labelName] : option[keyName]'
          v-html='option[labelName]'
          @click='() => selectOption(option, option.disabled)'
        )
      .row.top.m-top-1(v-if='showError')
        simple-svg.error-icon.red-icon.m4-right-2(:filepath='errorIcon' :width='"16px"' :height='"16px"')
        span.text-small.red
          | {{ errorMessage }}
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway'
import inputsValidationMixin from '@/mixins/inputsValidationMixin'
import errorIcon from '@/assets/ic_stop.svg'
import arrowDown from '@/assets/ic_chevron-down.svg'

export default {
  mixins: [ clickaway, inputsValidationMixin ],
  props: {
    name: { type: String, required: true },
    options: { type: Array, required: true },
    keyName: { type: String, default: 'id' },
    labelName: { type: String, default: 'label' },
    placeholder: { type: String },
    emptyOption: { type: String },
    preselectedOption: { type: [Number, String], default: null },
    noSpacing: { type: Boolean },
    withBorder: { type: Boolean },
    edition: { type: Boolean },
    error: { type: Boolean },
    errorMessage: { type: String },
    handleTextOverflow: { type: Boolean, default: false },
    disabled: { type: Boolean },
    label: { type: String },
    value: { type: Number },
    showRequiredMark: { type: Boolean },
    filled: { type: Boolean, required: false },
    small: { type: Boolean },
    big: { type: Boolean },
    labelIn: { type: String },
    transparent: { type: Boolean }
  },
  data() {
    return {
      errorIcon,
      arrowDown,
      showOptions: false,
      selected: this.preselectedOption || null
    }
  },
  watch: {
    preselectedOption() {
      this.selected = this.preselectedOption
    }
  },
  computed: {
    optionsToSelect() {
      return this.emptyOption
        ? [ { [this.keyName]: null, [this.labelName]: this.emptyOption, [this.keyName]: null }, ...this.options ]
        : this.options
    },
    selectedOption() {
      return this.optionsToSelect.find(option => option[this.keyName] === this.selected)
    }
  },
  methods: {
    toggleOptions() {
      this.showOptions = !this.showOptions
    },
    hideOptions() {
      this.showOptions = false
    },
    selectOption(option, disabled) {
      if (disabled) return
      if (option.onClick) {
        option.onClick()
        return this.toggleOptions()
      }
      this.selected = option[this.keyName]
      this.$emit('selectOption', this.name, this.selected)
      this.toggleOptions()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../scss/variables/colors';
@import '../scss/variables/z-indexes';
@import '../scss/common/forms';

.select-container {
  position: relative;
  width: 100%;
}

.no-spacing {
  padding: 0;
  margin: 0;
}

.select-button {
  @extend .select-base;
  align-items: center;
  display: flex;
  height: var(--input-height, 40px);
  justify-content: space-between;
  margin: 0;
  text-align: left;

  &:disabled{
    opacity: 0.6;
    cursor: not-allowed;
  }
  &.small{
    height: 30px;
    padding: 0 5px;
  }
  &.border {
    @extend .select-base;
    margin: 0;
    border: 1px solid $gray;
  }
  &.transparent{
    background-color: transparent;
  }
}

.filled{
  background-color: $lighter-blue;
}

.open {
  transform: rotate(180deg);
}

.options-container {
  background-color: $white;
  border: 1px solid $light-gray;
  border-radius: 6px;
  box-shadow: 0 2px 8px 0 $shadow-gray;
  max-height: 180px;
  overflow-y: auto;
  width: var(--select-container-width, 100%);
  z-index: $select-options;
  margin-top: 5px;
}

.option {
  align-items: center;
  color: $black;
  display: flex;
  min-height: var(--input-height, 40px);
  padding: 6px 14px;
  text-align: left;
  width: 100%;
  &.small{
    min-height: 35px;
  }
  &.disabled{
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:hover {
    background-color: $light-gray;
  }
}

.font-class {
  font-size: var(--font-size, 16px);
  line-height: var(--line-height, 19px);
}

.edition {
  .selected {
    color: $dark-gray;
  }
}

.error-container {
  border-color: $red;
}

.text-with-overflow {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>
